<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useDiscountDisplay } from '@bc/shared/composables/use-discount-display';

defineOptions({
  name: 'CardProductDiscountLabel',
});

const props = defineProps<{
  brandUuid?: string;
  hideVolumeDiscount?: boolean;
  /** Guaranteed brand-funded discount % (product-level `discount_rate`). */
  discountPercentage?: number;
  /** Whether the price block has a concrete discounted price to strike through. */
  hasDiscountedPrice?: boolean;
  /** Whether the wholesale price is visible to the user (logged-out users see no discount). */
  showPrice?: boolean;
  /** Whether the product is out of stock (discount is suppressed, like delivery). */
  outOfStock?: boolean;
}>();

const { state: discountState, isLoaded } = useDiscountDisplay({
  brandUuid: toRef(props, 'brandUuid'),
  discountPercentage: toRef(props, 'discountPercentage'),
  hasDiscountedPrice: toRef(props, 'hasDiscountedPrice'),
  hideVolumeDiscount: toRef(props, 'hideVolumeDiscount'),
});

const shouldShow = computed(
  () => !!props.showPrice && !props.outOfStock && isLoaded.value && discountState.value.hasDiscount
);
</script>

<template>
  <div
    v-if="shouldShow"
    class="card-product-discount-label"
    data-testid="discountLabel"
  >
    <template v-if="discountState.isUpTo">
      {{ $t('global.label.upToPercentageOff', { discount: discountState.displayPercentage }) }}
    </template>
    <template v-else>
      {{ $t('global.label.percentageOff', { discount: discountState.displayPercentage }) }}
    </template>
  </div>
</template>

<style lang="scss" scoped>
.card-product-discount-label {
  @apply ds-text-xs ds-font-semibold ds-text-discount-700 ds-my-1.5;
}
</style>
